import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { CheckCircleIcon, ExclamationCircleIcon } from './icons.js';
import styles from './Toast.module.css';

interface ToastItem {
  id: number;
  message: string;
  tone: 'default' | 'error';
  /** 나가는 길에 올랐다. 아직 화면에는 있다 */
  leaving: boolean;
}

interface ToastValue {
  show: (message: string, tone?: 'default' | 'error') => void;
}

const ToastContext = createContext<ToastValue | null>(null);

const VISIBLE_MS = 2600;

/**
 * 사라지는 데 걸리는 시간.
 *
 * ⚠️ `Toast.module.css`의 `toastOut`(`--fast`)과 **같아야 한다.** 여기가
 * 짧으면 다 사라지기 전에 지워져 끝이 잘린다.
 */
const LEAVING_MS = 120;

/**
 * 짧은 알림.
 *
 * 되돌릴 수 없는 일이나 사용자가 반드시 읽어야 하는 것에는 쓰지 않는다 —
 * 사라지기 때문이다. 그런 것은 화면 안에 남는 `Notice`로 보여준다.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, tone: 'default' | 'error' = 'default') => {
    const id = Date.now() + Math.random();
    setItems((current) => [...current, { id, message, tone, leaving: false }]);
    /*
     * 바로 지우지 않고 **나가는 길**을 한 번 거친다. 그냥 지우면 읽던 글이
     * 툭 없어져, 사라진 건지 화면이 깜빡인 건지 구분이 안 간다.
     */
    setTimeout(() => {
      setItems((current) =>
        current.map((item) => (item.id === id ? { ...item, leaving: true } : item)),
      );
      setTimeout(() => {
        setItems((current) => current.filter((item) => item.id !== id));
      }, LEAVING_MS);
    }, VISIBLE_MS);
  }, []);

  const value = useMemo<ToastValue>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* aria-live로 스크린 리더에도 전달한다. 화면에만 뜨면 절반만 알린 셈이다 */}
      <div className={styles.wrap} aria-live="polite" aria-atomic="false">
        {items.map((item) => (
          <div
            key={item.id}
            className={[
              styles.toast,
              item.tone === 'error' ? styles.error : '',
              item.leaving ? styles.leaving : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {/*
              잘 됐는지 아닌지를 **아이콘 색**으로만 말한다. 상자를 통째로
              빨갛게 칠하면 무채색으로 맞춰 둔 화면에서 사진보다 먼저 눈에
              들어온다. 글이 이미 무슨 일인지 말하므로 아이콘은 거들기만 하면
              된다 — 그래서 `aria-hidden`인 채로 둔다.
            */}
            {item.tone === 'error' ? (
              <ExclamationCircleIcon size={18} className={styles.icon} />
            ) : (
              <CheckCircleIcon size={18} className={styles.icon} />
            )}
            <span>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error('ToastProvider 안에서만 쓸 수 있습니다');
  }
  return value;
}
